import { useState, useEffect, useRef, useMemo, Fragment } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormField } from "@/components/ui-system";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Save,
  Download,
  Upload,
  AlertTriangle,
  Loader2,
  Table2,
} from "lucide-react";

const ZONES = ["A", "B", "C", "D", "E", "F"];
const MODES = ["surface", "air"];

const emptyZoneDict = () =>
  ZONES.reduce((acc, z) => ({ ...acc, [z]: null }), {});

/**
 * Coerce a text input value to a number|null. Blank string => null (zone not served).
 */
const toNumOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

/**
 * Normalize one rate_card row coming from the API into the editor's shape, ensuring
 * all six A–F keys exist on every zone dict.
 */
const normalizeRow = (row) => {
  const fill = (dict) => {
    const base = emptyZoneDict();
    if (dict && typeof dict === "object") {
      ZONES.forEach((z) => {
        if (dict[z] !== undefined) base[z] = dict[z];
      });
    }
    return base;
  };
  return {
    _uid: row._uid || `${row.courier_code}|${row.mode}|${row.slab_kg}|${Math.random().toString(36).slice(2, 8)}`,
    courier_code: row.courier_code ?? "",
    mode: row.mode ?? "surface",
    slab_kg: row.slab_kg ?? 0.5,
    incr_unit_kg: row.incr_unit_kg ?? 0.5,
    fwd: fill(row.fwd),
    fwd_incr: fill(row.fwd_incr),
    rto: fill(row.rto),
    rto_incr: fill(row.rto_incr),
    cod_flat: row.cod_flat ?? 0,
    cod_pct: row.cod_pct ?? 0,
  };
};

const allNull = (dict) => ZONES.every((z) => toNumOrNull(dict?.[z]) === null);

/**
 * Build the API payload row from an editor row: blank zone cells become null.
 * rto/rto_incr are OMITTED when entirely unset so the backend mirrors the
 * forward rates (matches the CSV "blank rto = copy fwd" convention); sending an
 * all-null rto dict would persist RTO as "not served" instead.
 */
const serializeRow = (row) => {
  const z = (dict) =>
    ZONES.reduce((acc, k) => ({ ...acc, [k]: toNumOrNull(dict?.[k]) }), {});
  const out = {
    courier_code: row.courier_code,
    mode: row.mode,
    slab_kg: Number(row.slab_kg),
    incr_unit_kg: Number(row.incr_unit_kg),
    fwd: z(row.fwd),
    fwd_incr: z(row.fwd_incr),
    cod_flat: Number(row.cod_flat) || 0,
    cod_pct: Number(row.cod_pct) || 0,
  };
  if (!allNull(row.rto)) out.rto = z(row.rto);
  if (!allNull(row.rto_incr)) out.rto_incr = z(row.rto_incr);
  return out;
};

/**
 * Pre-save validation mirroring the CSV parser's rules:
 * - incr_unit_kg > 0 (a blank cell would 422 the whole card with no location);
 * - at least one forward zone served.
 * Increment rates are OPTIONAL: a slab with a forward rate but no increment is a
 * FLAT slab (the rate covers up to the slab weight; there is no per-kg add-on).
 * The rate engine prices such a slab for weights up to the slab and simply
 * returns no quote for heavier parcels on that row — a larger slab then covers
 * them, or the courier is unserviceable above the slab. That is an intentional
 * choice, not an error, so we no longer force an increment to be entered.
 * Returns a human-readable error string (naming the row), or null when valid.
 */
const validateRows = (rowsToCheck, courierName) => {
  for (const row of rowsToCheck) {
    const label = `${courierName?.[row.courier_code] || row.courier_code} ${row.mode} ${row.slab_kg}kg`;
    const incrUnit = Number(row.incr_unit_kg);
    if (!Number.isFinite(incrUnit) || incrUnit <= 0) {
      return `${label}: increment unit (kg) must be greater than 0`;
    }
    const fwdServed = ZONES.filter((z) => toNumOrNull(row.fwd?.[z]) !== null);
    if (fwdServed.length === 0) {
      return `${label}: at least one forward zone rate is required`;
    }
  }
  return null;
};

const NumCell = ({ value, onChange, disabled, testid, placeholder = "—", className = "" }) => (
  <Input
    type="number"
    inputMode="decimal"
    step="any"
    value={value === null || value === undefined ? "" : value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    placeholder={placeholder}
    data-testid={testid}
    className={`h-8 w-16 px-1.5 text-right text-xs tabular-nums ${className}`}
  />
);

/**
 * The data-dense rate-card grid editor + CSV import/template controls.
 *
 * props: plan (the selected plan), couriers (canonical registry), canManage (bool),
 *        onSaved (cb after PUT / CSV import reloads the plan)
 */
export function RateCardEditor({ plan, couriers, canManage, onSaved }) {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("fwd"); // 'fwd' | 'rto'
  const [showIncr, setShowIncr] = useState(true); // increments visible by default; "Hide increments" collapses them
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Add-row dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ courier_code: "", mode: "surface", slab_kg: "0.5" });

  // CSV import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState(null); // [{row,error}] | null
  const fileRef = useRef(null);

  useEffect(() => {
    setRows((plan?.rate_card || []).map(normalizeRow));
    setDirty(false);
    setImportErrors(null);
  }, [plan?.id, plan?.version]);

  const courierName = useMemo(() => {
    const m = {};
    (couriers || []).forEach((c) => {
      m[c.code] = c.name;
    });
    return m;
  }, [couriers]);

  const zoneKey = view === "fwd" ? "fwd" : "rto";
  const incrKey = view === "fwd" ? "fwd_incr" : "rto_incr";

  const updateRowField = (uid, field, value) => {
    setRows((prev) => prev.map((r) => (r._uid === uid ? { ...r, [field]: value } : r)));
    setDirty(true);
  };

  const updateZoneCell = (uid, dictKey, zone, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r._uid === uid ? { ...r, [dictKey]: { ...r[dictKey], [zone]: value } } : r
      )
    );
    setDirty(true);
  };

  const handleAddRow = () => {
    if (!addForm.courier_code) {
      toast.error("Pick a courier");
      return;
    }
    const slab = Number(addForm.slab_kg);
    if (!(slab > 0)) {
      toast.error("Slab (kg) must be greater than 0");
      return;
    }
    const dup = rows.some(
      (r) =>
        r.courier_code === addForm.courier_code &&
        r.mode === addForm.mode &&
        Number(r.slab_kg) === slab
    );
    if (dup) {
      toast.error("A row with this courier + mode + slab already exists");
      return;
    }
    setRows((prev) => [
      ...prev,
      normalizeRow({ courier_code: addForm.courier_code, mode: addForm.mode, slab_kg: slab }),
    ]);
    setDirty(true);
    setAddOpen(false);
    setAddForm({ courier_code: "", mode: "surface", slab_kg: "0.5" });
  };

  const handleDeleteRow = (uid) => {
    setRows((prev) => prev.filter((r) => r._uid !== uid));
    setDirty(true);
  };

  const handleSave = async () => {
    const validationError = validateRows(rows, courierName);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSaving(true);
    try {
      const payload = { rate_card: rows.map(serializeRow) };
      const res = await axios.put(`${API}/b2c/admin/plans/${plan.id}`, payload);
      toast.success(`Rate card saved (v${res.data?.version ?? "?"})`);
      setDirty(false);
      onSaved?.(res.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail
              .map((d) => {
                const loc = Array.isArray(d.loc) ? d.loc.slice(-3).join(".") : "";
                return loc ? `${loc}: ${d.msg}` : d.msg || JSON.stringify(d);
              })
              .join("; ")
          : "Failed to save rate card"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/b2c/admin/plans/rate-card-template`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "b2c-rate-card-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download template");
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File too large — 2MB max");
      return;
    }
    setImporting(true);
    setImportErrors(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/b2c/admin/plans/${plan.id}/rate-card/import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Rate card imported (v${res.data?.version ?? "?"})`);
      setImportOpen(false);
      onSaved?.(res.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail) && detail.length && detail[0]?.row !== undefined) {
        setImportErrors(detail);
        toast.error(`${detail.length} row error(s) — fix the sheet and re-import`);
      } else {
        setImportErrors(null);
        toast.error(typeof detail === "string" ? detail : "CSV import failed");
      }
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-slate-700">Rate card</span>
          <Badge variant="outline" className="text-[11px]">
            v{plan?.version ?? 0}
          </Badge>
          <Badge variant="outline" className="text-[11px]">
            {rows.length} row{rows.length === 1 ? "" : "s"}
          </Badge>
          {dirty && (
            <Badge className="bg-amber-100 text-amber-800 border-0 text-[11px]" data-testid="rc-unsaved-indicator">
              <AlertTriangle className="mr-1 h-3 w-3" />
              Unsaved changes
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Forward / RTO toggle */}
          <div className="inline-flex rounded-md border bg-white p-0.5" role="group" aria-label="Direction">
            <button
              type="button"
              onClick={() => setView("fwd")}
              data-testid="rc-view-fwd"
              className={`px-3 py-1 text-xs font-medium rounded ${
                view === "fwd" ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              Forward
            </button>
            <button
              type="button"
              onClick={() => setView("rto")}
              data-testid="rc-view-rto"
              className={`px-3 py-1 text-xs font-medium rounded ${
                view === "rto" ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              RTO
            </button>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowIncr((s) => !s)}
            data-testid="rc-toggle-incr"
          >
            {showIncr ? "Hide" : "Show"} increments
          </Button>
        </div>
      </div>

      {/* CSV controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="rc-download-template">
          <Download className="mr-1.5 h-4 w-4" />
          Template
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setImportErrors(null);
            setImportOpen(true);
          }}
          disabled={!canManage}
          data-testid="rc-open-import"
        >
          <Upload className="mr-1.5 h-4 w-4" />
          Import CSV
        </Button>
        <div className="ml-auto flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddOpen(true)}
            disabled={!canManage}
            data-testid="rc-add-row"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add row
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={!canManage || !dirty || saving}
            data-testid="rc-save"
          >
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Save card
          </Button>
        </div>
      </div>

      {/* Grid */}
      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed bg-white px-4 py-12 text-center">
          <Table2 className="mb-3 h-12 w-12 text-slate-300" strokeWidth={1.5} />
          <p className="text-sm font-medium text-slate-600">No rate-card rows yet</p>
          <p className="mt-1 text-xs text-slate-500">
            Add rows manually or import a CSV. Each row is a courier + mode + weight slab.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="min-w-[760px] border-collapse text-xs">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="sticky left-0 z-10 bg-slate-50 px-3 py-2 text-left font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap min-w-[180px]">
                  Courier / Slab
                </th>
                <th className="px-3 py-2 text-center font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">
                  Incr unit
                </th>
                {ZONES.map((z) => (
                  <th key={z} className="px-3 py-2 text-center font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">
                    {view === "fwd" ? "Fwd " : "RTO "}
                    {z}
                  </th>
                ))}
                <th className="px-3 py-2 text-center font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">
                  COD ₹
                </th>
                <th className="px-3 py-2 text-center font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">
                  COD %
                </th>
                <th className="px-3 py-2 text-center font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap" aria-label="Actions" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <Fragment key={row._uid}>
                  <tr className="hover:bg-slate-50/60">
                    <td className="sticky left-0 z-10 bg-white px-3 py-2 align-top min-w-[180px]">
                      <div className="text-sm font-medium text-slate-800">
                        {courierName[row.courier_code] || row.courier_code}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                        <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                          {row.mode}
                        </Badge>
                        <span className="tabular-nums">{row.slab_kg} kg slab</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center align-middle">
                      <NumCell
                        value={row.incr_unit_kg}
                        onChange={(v) => updateRowField(row._uid, "incr_unit_kg", v)}
                        disabled={!canManage}
                        testid={`rc-incrunit-${row._uid}`}
                        placeholder="kg"
                      />
                    </td>
                    {ZONES.map((z) => (
                      <td key={z} className="px-3 py-2 text-center align-middle">
                        <NumCell
                          value={row[zoneKey][z]}
                          onChange={(v) => updateZoneCell(row._uid, zoneKey, z, v)}
                          disabled={!canManage}
                          testid={`rc-${view}-${z}-${row._uid}`}
                        />
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center align-middle">
                      <NumCell
                        value={row.cod_flat}
                        onChange={(v) => updateRowField(row._uid, "cod_flat", v)}
                        disabled={!canManage}
                        testid={`rc-codflat-${row._uid}`}
                        placeholder="0"
                      />
                    </td>
                    <td className="px-3 py-2 text-center align-middle">
                      <NumCell
                        value={row.cod_pct}
                        onChange={(v) => updateRowField(row._uid, "cod_pct", v)}
                        disabled={!canManage}
                        testid={`rc-codpct-${row._uid}`}
                        placeholder="0"
                      />
                    </td>
                    <td className="px-3 py-2 text-center align-middle">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-500 hover:text-red-600"
                        onClick={() => handleDeleteRow(row._uid)}
                        disabled={!canManage}
                        aria-label="Delete row"
                        data-testid={`rc-delete-${row._uid}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                  {showIncr && (
                    <tr className="bg-slate-50/40">
                      <td className="sticky left-0 z-10 bg-slate-50 px-3 py-2 text-xs italic text-slate-500 min-w-[180px]">
                        ↳ per-{row.incr_unit_kg}kg increment
                      </td>
                      <td className="px-3 py-2" aria-hidden="true" />
                      {ZONES.map((z) => (
                        <td key={z} className="px-3 py-2 text-center align-middle">
                          <NumCell
                            value={row[incrKey][z]}
                            onChange={(v) => updateZoneCell(row._uid, incrKey, z, v)}
                            disabled={!canManage}
                            testid={`rc-${view}incr-${z}-${row._uid}`}
                          />
                        </td>
                      ))}
                      <td className="px-3 py-2" aria-hidden="true" />
                      <td className="px-3 py-2" aria-hidden="true" />
                      <td className="px-3 py-2" aria-hidden="true" />
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-400">
        Blank cell = zone not served (null). Toggle Forward / RTO to edit each direction; RTO defaults to Forward
        when left blank on import.
      </p>

      {/* Add row dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add rate-card row</DialogTitle>
            <DialogDescription>Pick a canonical courier, mode and weight slab.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <FormField label="Courier" htmlFor="rc-add-courier" required>
              <Select
                value={addForm.courier_code}
                onValueChange={(v) => setAddForm((f) => ({ ...f, courier_code: v }))}
              >
                <SelectTrigger id="rc-add-courier" data-testid="rc-add-courier">
                  <SelectValue placeholder="Select courier" />
                </SelectTrigger>
                <SelectContent>
                  {(couriers || []).length === 0 ? (
                    <div className="px-3 py-2 text-xs text-slate-500">No couriers in registry yet</div>
                  ) : (
                    couriers.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.name} ({c.code})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Mode" htmlFor="rc-add-mode" required>
              <Select value={addForm.mode} onValueChange={(v) => setAddForm((f) => ({ ...f, mode: v }))}>
                <SelectTrigger id="rc-add-mode" data-testid="rc-add-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Weight slab (kg)" htmlFor="rc-add-slab" required hint="Base slab weight, must be > 0">
              <Input
                id="rc-add-slab"
                type="number"
                step="any"
                min="0"
                value={addForm.slab_kg}
                onChange={(e) => setAddForm((f) => ({ ...f, slab_kg: e.target.value }))}
                data-testid="rc-add-slab"
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAddOpen(false)} data-testid="rc-add-cancel">
              Cancel
            </Button>
            <Button type="button" onClick={handleAddRow} data-testid="rc-add-confirm">
              Add row
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Import rate card (CSV)</DialogTitle>
            <DialogDescription>
              All-or-nothing import: a single bad row rejects the whole file. 2MB / ~2000 rows max. This replaces the
              entire rate card and bumps the version.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="rc-import-template">
                <Download className="mr-1.5 h-4 w-4" />
                Download template
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rc-import-file" className="text-sm font-medium text-slate-700">
                CSV file
              </Label>
              <Input
                id="rc-import-file"
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                disabled={importing || !canManage}
                onChange={(e) => handleImport(e.target.files?.[0])}
                data-testid="rc-import-file"
              />
              {importing && (
                <p className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Validating and importing…
                </p>
              )}
            </div>

            {importErrors && (
              <div className="rounded-lg border border-red-200 bg-red-50">
                <div className="flex items-center gap-2 border-b border-red-200 px-3 py-2 text-sm font-semibold text-red-700">
                  <AlertTriangle className="h-4 w-4" />
                  {importErrors.length} row error{importErrors.length === 1 ? "" : "s"} — nothing was imported
                </div>
                <div className="max-h-56 overflow-y-auto" data-testid="rc-import-errors">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-red-100/80">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-red-700 w-16">Row</th>
                        <th className="px-3 py-2 text-left font-semibold text-red-700">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importErrors.map((e, i) => (
                        <tr key={i} className="border-t border-red-100">
                          <td className="px-3 py-2 align-top tabular-nums text-red-700">{e.row}</td>
                          <td className="px-3 py-2 align-top text-red-800 break-words">{e.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setImportOpen(false)} data-testid="rc-import-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RateCardEditor;
